import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { InvoiceSummary, WaSessionInfo } from '../types';

type Channel = 'wa' | 'email';

/**
 * Send a payment reminder for an outstanding invoice. Compact dialog —
 * pick channel (WA when paired, else email), edit a prefilled body that
 * already names the invoice + amount + Greeninvoice PDF URL, send.
 *
 * Doesn't attach a Document: the invoice PDF lives at Greeninvoice as a
 * remote URL, not in our R2/Document table. The body just links to it.
 */
export function InvoiceReminderModal({
  invoice,
  clientId,
  firmId,
  onSent,
  onClose,
}: {
  invoice: InvoiceSummary;
  clientId: string | null;
  firmId: string;
  onSent: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['cases', 'billing', 'common']);

  const [waInfo, setWaInfo] = useState<WaSessionInfo | null>(null);
  const waConnected = waInfo?.status === 'connected';
  const [channel, setChannel] = useState<Channel>('email');

  const amount = (invoice.totalAgorot / 100).toLocaleString(i18n.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const number = invoice.greeninvoiceNumber ?? invoice.id.slice(0, 8);
  const dueAt = invoice.dueAt
    ? new Date(invoice.dueAt).toLocaleDateString(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  const defaultBody = t('detail.invoice_reminder.body_template', {
    number,
    amount,
    due: dueAt ?? '—',
    pdf: invoice.documentUrl ?? '',
  }).trim();
  const defaultSubject = t('detail.invoice_reminder.subject_template', { number });

  const [body, setBody] = useState(defaultBody);
  const [subject, setSubject] = useState(defaultSubject);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<WaSessionInfo>(`/api/firms/${firmId}/wa/status`)
      .then(setWaInfo)
      .catch(() => setWaInfo(null));
  }, [firmId]);

  // Default channel: WA if paired, else email.
  useEffect(() => {
    if (waConnected) setChannel('wa');
  }, [waConnected]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!clientId) {
      setError(t('detail.invoice_reminder.no_client'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (channel === 'wa') {
        await apiFetch('/api/messages/wa', {
          method: 'POST',
          body: JSON.stringify({
            clientId,
            ...(invoice.caseId ? { caseId: invoice.caseId } : {}),
            body,
          }),
        });
      } else {
        await apiFetch('/api/messages/email', {
          method: 'POST',
          body: JSON.stringify({
            clientId,
            ...(invoice.caseId ? { caseId: invoice.caseId } : {}),
            subject,
            body,
          }),
        });
      }
      onSent();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div className="modal-header">
          <h2>{t('detail.invoice_reminder.title', { number })}</h2>
          <button className="modal-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-section">
          {/* Channel toggle */}
          <div className="field">
            <label>{t('detail.invoice_reminder.channel')}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className={channel === 'wa' ? 'btn-primary' : 'btn-cancel'}
                disabled={!waConnected}
                onClick={() => setChannel('wa')}
                style={{ padding: '7px 14px', fontSize: 12 }}
                title={!waConnected ? t('detail.invoice_reminder.wa_disabled') : undefined}
              >
                WhatsApp
              </button>
              <button
                type="button"
                className={channel === 'email' ? 'btn-primary' : 'btn-cancel'}
                onClick={() => setChannel('email')}
                style={{ padding: '7px 14px', fontSize: 12 }}
              >
                {t('detail.invoice_reminder.email')}
              </button>
            </div>
            {!waConnected && channel === 'wa' && (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                {t('detail.invoice_reminder.wa_disabled')}
              </div>
            )}
          </div>

          {channel === 'email' && (
            <div className="field">
              <label>{t('detail.invoice_reminder.subject')}</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>
          )}

          <div className="field">
            <label>{t('detail.invoice_reminder.body')}</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              required
              dir="auto"
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !clientId}>
              {busy ? t('common:actions.saving') : t('detail.action_send_reminder')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
